import {ReactElement, ReactNode, useEffect, useMemo, useRef, useState} from "react";
import * as css from "./styles/unitTypeManagement.module.scss";
import useAppStore from "../../../../hooks/useAppStore";

import {Field, MediaInput} from '@zendeskgarden/react-forms';
import {Button, IconButton} from "@zendeskgarden/react-buttons";

import Card from "../../../simplified-revenue/components/Card";
import {Property} from "../../../../contexts/properties/PropertiesContext";
import {
    PropertyType,
    UnitTypeForPmsActualOverridesUpdateInput,
    useListUnitTypesLazyQuery,
    useSetUnitTypePmsOverridesMutation,
    useUpdateUnitTypeMutation,
    useAddUnitTypesMutation,
    useDeleteUnitTypesMutation
} from "../../../../__generated__/generated_types";
import {ReactComponent as SearchIcon} from '@zendeskgarden/svg-icons/src/16/search-stroke.svg';
import {ReactComponent as PlusIcon} from '@zendeskgarden/svg-icons/src/16/plus-stroke.svg';
import {ReactComponent as AlertIcon} from '@zendeskgarden/svg-icons/src/16/alert-warning-fill.svg';
import {EditUnitType} from "./EditUnitType";
import {toast} from "react-toastify";
import {Link} from "react-router-dom";
import Handsontable from "handsontable";
import {BaseRenderer} from "handsontable/renderers";
import {
    ContextMenuItem,
    SimpleHoTChange,
    SimpleHotColumnSettings,
    SimpleHoTWrapper
} from "../../../admin/line-item-management/SimpleHoTWrapper";
import "../../../../components/vz-ui/vz-hot-table/styles/vz-hot-table.scss";
import ConfirmationModal from "../../../../components/confirmation-modal/ConfirmationModal";
import {
    buildUnitTypeTableRowsAndPmsData,
    COL_IN_PLACE_RENT,
    COL_NAME,
    COL_OCCUPANCY,
    COL_SELECT,
    COL_UNIT_COUNT,
    columnIndex,
    metricHasOverride,
    ParsedData,
    parseNumericInput,
    round2,
    UnitTypeTableRow
} from "./logic";
import VZSaveStatus from "../../../../components/vz-ui/vz-save-status/VZSaveStatus";
import {Tooltip} from "@zendeskgarden/react-tooltips";
import {useRoutingUtils} from "../../../../routes/RoutingUtils";

export type IUnitTypesParams = {
    property: Property;
    budgetYear: number;
    customLabelComponent?: ReactNode;
};

export default function UnitTypes(props: IUnitTypesParams): ReactElement {
    const appStore = useAppStore();
    const {property} = props;
    const {goto} = useRoutingUtils();
    const [getUnitTypes, {data: unitTypesData, loading: unitTypesDataLoading}] = useListUnitTypesLazyQuery({fetchPolicy: "no-cache"});
    const [deleteUnitTypes, {loading: deleteUnitTypesLoading}] = useDeleteUnitTypesMutation({notifyOnNetworkStatusChange: true});
    const [updateUnitType, {loading: updateUnitTypeLoading}] = useUpdateUnitTypeMutation({notifyOnNetworkStatusChange: true});
    const [updateUnitTypeOverrides, {loading: updateUnitTypeOverridesLoading}] = useSetUnitTypePmsOverridesMutation();
    const [addUnitTypes, {loading: addUnitTypesLoading}] = useAddUnitTypesMutation({notifyOnNetworkStatusChange: true});
    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const hotRef = useRef<Handsontable | null>(null);
    const pmsAlertIconTemplateRef = useRef<HTMLSpanElement>(null);

    const [parsedData, setParsedData] = useState<ParsedData | undefined>(undefined);
    const [selectedUnitTypeIds, setSelectedUnitTypeIds] = useState<Set<string>>(new Set());
    const [unitTypeModal, setUnitTypeModal] = useState<{
        unitTypeId?: string;
        initialName?: string;
        title: string;
    }>();
    const [deleteUnitTypeData, setDeleteUnitTypeData] = useState<{id: string, name: string;}[]>([]);
    const [searchText, setSearchText] = useState("");

    const {lastActualMonthIndex, actualsYear} = useMemo(() => {
        let lastActualMonthIndex = property.reforecastStartMonthIndex - 1;
        let actualsYear = props.budgetYear - 1;
        if(lastActualMonthIndex < 0) {
            lastActualMonthIndex = 11;
            actualsYear = props.budgetYear - 2;
        }
        return {lastActualMonthIndex, actualsYear};
    }, [property.reforecastStartMonthIndex, props.budgetYear]);

    function fetchUnitTypesData() {
        getUnitTypes({
            variables: {
                propertyId: property.id,
                monthIndex: lastActualMonthIndex,
                budgetYear: props.budgetYear,
                actualsYear: actualsYear
            }
        });
    }

    type MetricOverrideUpdate = {
        unitTypeId: string;
        unitCountOverrides: Record<number, number | null>;
        occupancyOverrides: Record<number, number | null>;
        inPlaceRentOverrides: Record<number, number | null>;
    };

    function saveOverrides(updates: MetricOverrideUpdate[]) {
        if(updates.length === 0) {
            return;
        }
        const unitCountById: Record<string, UnitTypeForPmsActualOverridesUpdateInput> = {};
        const occupancyById: Record<string, UnitTypeForPmsActualOverridesUpdateInput> = {};
        const inPlaceRentById: Record<string, UnitTypeForPmsActualOverridesUpdateInput> = {};

        function mergeMonthOverrides(
            byId: Record<string, UnitTypeForPmsActualOverridesUpdateInput>,
            unitTypeId: string,
            overrides: Record<number, number | null>
        ) {
            for(const [monthIndex, val] of Object.entries(overrides)) {
                let dbUpdate: UnitTypeForPmsActualOverridesUpdateInput | undefined = byId[unitTypeId];

                if(!dbUpdate) {
                    dbUpdate = {unitTypeId};
                    byId[unitTypeId] = dbUpdate;
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dbUpdate[`valueM${monthIndex}`] = val;
            }
        }

        for(const update of updates) {
            mergeMonthOverrides(unitCountById, update.unitTypeId, update.unitCountOverrides);
            mergeMonthOverrides(occupancyById, update.unitTypeId, update.occupancyOverrides);
            mergeMonthOverrides(inPlaceRentById, update.unitTypeId, update.inPlaceRentOverrides);
        }

        const unitCountUpdates = Object.values(unitCountById);
        const occupancyUpdates = Object.values(occupancyById);
        const inPlaceRentUpdates = Object.values(inPlaceRentById);
        if(unitCountUpdates.length === 0 && occupancyUpdates.length === 0 && inPlaceRentUpdates.length === 0) {
            return;
        }
        updateUnitTypeOverrides({
            variables: {
                propertyId: property.id,
                unitCount: unitCountUpdates,
                occupancy: occupancyUpdates,
                inPlaceRent: inPlaceRentUpdates,
                budgetYear: props.budgetYear
            }
        })
        .then(() => {
            fetchUnitTypesData();
        })
        .catch(() => {
            toast.error(`Unit Type update failed`);
            fetchUnitTypesData();
        });
    }

    async function handleDeleteUnitTypes(items: {id: string, name: string;}[]) {
        if(items.length === 0) {
            return;
        }
        deleteUnitTypes({
            variables: {
                propertyId: property.id,
                unitTypeIds: items.map(item => item.id),
                budgetYear: props.budgetYear
            }
        })
        .then(() => {
            setDeleteUnitTypeData([]);
            fetchUnitTypesData();
        })
        .catch(() => {
            toast.error("Failed to delete one or more unit types");
            setDeleteUnitTypeData([]);
            fetchUnitTypesData();
        });
    }

    async function handleAddUnitTypes(names: string[]) {
        if(!property || names.length === 0) {
            return;
        }
        addUnitTypes({
            variables: {
                propertyId: property.id,
                names: names,
                budgetYear: props.budgetYear
            }
        })
        .then(() => {
            setLastSaved(new Date());
            setUnitTypeModal(undefined);
            fetchUnitTypesData();
        })
        .catch(() => {
            toast.error("Failed to add one or more unit types");
            fetchUnitTypesData();
        });
    }

    useEffect(
        () => {
            appStore.set({isLoading: false});
            return () => undefined;
        },
        []
    );

    useEffect(() => {
        fetchUnitTypesData();
    }, [property.id, lastActualMonthIndex, actualsYear, props.budgetYear]);

    useEffect(() => {
        if(!unitTypesData || unitTypesDataLoading) {
            return;
        }
        setSelectedUnitTypeIds(new Set());
        setParsedData(buildUnitTypeTableRowsAndPmsData(unitTypesData));
        setLastSaved(new Date());
    }, [unitTypesData, unitTypesDataLoading]);

    const tableRowsDisplay = useMemo<UnitTypeTableRow[]>(() => {
        const normalized = searchText.trim().toLowerCase();
        if(normalized.length === 0) {
            return parsedData?.unitTypeTableRows ?? [];
        }
        return parsedData?.unitTypeTableRows.filter(row => row.values.name.toLowerCase().includes(normalized)) ?? [];
    }, [parsedData, searchText]);

    const selectedUnitTypes = useMemo(() => {
        if(selectedUnitTypeIds.size === 0 || !parsedData) {
            return [];
        }
        return parsedData.unitTypeTableRows.filter(row => selectedUnitTypeIds.has(row.extraData.id));
    }, [selectedUnitTypeIds, parsedData]);

    const canEditSelected = selectedUnitTypes.length === 1;
    const canDeleteSelected = selectedUnitTypes.length > 0;

    const tableColumns = useMemo<SimpleHotColumnSettings[]>(() => [
        {data: "select", readOnly: true},
        {data: "name", readOnly: true},
        {data: "unitCount", type: "numeric", allowInvalid: false},
        {data: "occupancy", type: "numeric", allowInvalid: false},
        {data: "inPlaceRent", type: "numeric", allowInvalid: false}
    ], []);

    function toggleUnitTypeSelected(unitTypeId: string) {
        setSelectedUnitTypeIds(prev => {
            const updated = new Set(prev);
            if(updated.has(unitTypeId)) {
                updated.delete(unitTypeId);
            } else {
                updated.add(unitTypeId);
            }
            return updated;
        });
    }

    function openDeleteSelectedUnitTypes() {
        if(selectedUnitTypes.length === 0) {
            return;
        }
        setDeleteUnitTypeData(selectedUnitTypes.map(row => ({id: row.extraData.id, name: row.values.name})));
    }

    function handleUnitTypeModalSubmit(names: string[]) {
        if(!unitTypeModal || names.length === 0) {
            return;
        }
        if(unitTypeModal.unitTypeId) {
            const nextName = names[0]?.trim();
            if(!nextName) {
                return;
            }
            if(nextName === unitTypeModal.initialName) {
                setUnitTypeModal(undefined);
                return;
            }
            updateUnitType({
                variables: {
                    unitTypeId: unitTypeModal.unitTypeId,
                    name: nextName,
                    budgetYear: props.budgetYear
                }
            })
                .then(() => {
                    toast.success(`Unit Type update complete`);
                    setUnitTypeModal(undefined);
                    fetchUnitTypesData();
                })
                .catch(() => {
                    toast.error(`Unit Type update failed`);
                    fetchUnitTypesData();
                });
            return;
        }
        handleAddUnitTypes(names);
    }

    function updateMetricOverride(
        row: UnitTypeTableRow,
        col: number,
        newValue: number | null
    ): MetricOverrideUpdate | undefined {
        const unitCountOverrides: Record<number, number | null> = {};
        const occupancyOverrides: Record<number, number | null> = {};
        const inPlaceRentOverrides: Record<number, number | null> = {};

        if(col === COL_UNIT_COUNT) {
            unitCountOverrides[lastActualMonthIndex] = newValue;
        } else if(col === COL_OCCUPANCY) {
            const stored = newValue === null ? null : newValue / 100;
            occupancyOverrides[lastActualMonthIndex] = stored;
        } else if(col === COL_IN_PLACE_RENT) {
            inPlaceRentOverrides[lastActualMonthIndex] = newValue;
        } else {
            return undefined;
        }

        return {
            unitTypeId: row.extraData.id,
            unitCountOverrides,
            occupancyOverrides,
            inPlaceRentOverrides
        };
    }

    function handleBeforeChange(changes: SimpleHoTChange[]): SimpleHoTChange[] {
        const accepted: SimpleHoTChange[] = [];

        let hasInvalidChange = false;

        for(const change of changes) {
            const row = change.row as UnitTypeTableRow;
            const col = columnIndex(change.col);

            if(col === COL_UNIT_COUNT || col === COL_OCCUPANCY || col === COL_IN_PLACE_RENT) {
                const parsed = parseNumericInput(change.newValue);
                if(parsed === undefined) {
                    hasInvalidChange = true;
                    continue;
                }
                if(parsed === null) {
                    accepted.push({...change, newValue: 0});
                    continue;
                }
                accepted.push({...change, newValue: round2(parsed)});
                continue;
            }
        }
        if(hasInvalidChange) {
            toast.error("Invalid number");
        }
        return accepted;
    }

    function handleChange(changes: SimpleHoTChange[]): void {
        const overrideUpdates: MetricOverrideUpdate[] = [];

        for(const change of changes) {
            const row = change.row as UnitTypeTableRow;
            const col = columnIndex(change.col);

            if(col === COL_UNIT_COUNT || col === COL_OCCUPANCY || col === COL_IN_PLACE_RENT) {
                const parsed = parseNumericInput(change.newValue);
                if(parsed === undefined) {
                    continue;
                }
                const update = updateMetricOverride(row, col, parsed);
                if(update) {
                    overrideUpdates.push(update);
                }
            }
        }

        saveOverrides(overrideUpdates);
    }

    const contextMenu = useMemo<Record<string, ContextMenuItem>>(() => ({
        revertOverride: {
            name: "Revert to original",
            hidden: (_rows, cols, selectedRowData) => {
                const metricCols = Array.from(cols).filter(c => c === COL_UNIT_COUNT || c === COL_OCCUPANCY || c === COL_IN_PLACE_RENT);
                if(metricCols.length === 0) {
                    return true;
                }
                return !selectedRowData.some(sel =>
                    metricCols.some(col => metricHasOverride(sel.rowData as UnitTypeTableRow, col))
                );
            },
            disabled: () => false,
            callback: (selectedRows, selectedCols) => {
                const hot = hotRef.current;
                if(!hot) {
                    return;
                }
                const metricCols = Array.from(selectedCols).filter(c => c === COL_UNIT_COUNT || c === COL_OCCUPANCY || c === COL_IN_PLACE_RENT);
                const overrideUpdates: MetricOverrideUpdate[] = [];

                for(const rowIndex of Array.from(selectedRows)) {
                    const row = parsedData?.unitTypeTableRows[rowIndex] as UnitTypeTableRow | undefined;
                    if(!row) {
                        continue;
                    }
                    for(const col of metricCols) {
                        if(!metricHasOverride(row, col)) {
                            continue;
                        }
                        const update = updateMetricOverride(row, col, null);
                        if(update) {
                            overrideUpdates.push(update);
                        }
                    }
                }
                saveOverrides(overrideUpdates);
            }
        }
    }), [parsedData, lastActualMonthIndex, property.id, props.budgetYear]);

    return (
        <>
            <span ref={pmsAlertIconTemplateRef} className={css.nameAlertIconTemplate} aria-hidden>
                <AlertIcon style={{color: "var(--yellow-200)"}} />
            </span>
            <div className={css.header}>
                {props.customLabelComponent}
                <VZSaveStatus isSaving={unitTypesDataLoading || deleteUnitTypesLoading || updateUnitTypeLoading || updateUnitTypeOverridesLoading || addUnitTypesLoading} lastSaved={lastSaved} />
            </div>

            <Card
                title={`Unit Types For Property ${property?.name ?? ""}`}
                actions={
                    <>
                        {property && unitTypesData && !unitTypesDataLoading && !unitTypesData.findPMSProperty && property.propertyType === PropertyType.Standard &&
                            <Tooltip content={`Property not known in PMS. Click to fix it.`} delayMS={0} hasArrow={false} placement={"top"} zIndex={110}>
                                <Link to={"/settings/property-management"}>
                                    <AlertIcon style={{color: "var(--yellow-200)", marginRight: "0.5rem"}}/>
                                </Link>
                            </Tooltip>
                        }
                        <Field className={css.inputBox}>
                            <MediaInput start={<SearchIcon />} placeholder="Search" onChange={e => {
                                setSearchText(e.target.value);
                            }} />
                        </Field>
                        <Button
                            isBasic
                            disabled={!canEditSelected}
                            onClick={() => {
                                const selected = selectedUnitTypes[0];
                                if(!selected) {
                                    return;
                                }
                                setUnitTypeModal({
                                    unitTypeId: selected.extraData.id,
                                    initialName: selected.values.name,
                                    title: `Edit Unit Type ${selected.values.name}`
                                });
                            }}
                            style={{marginLeft: "0.5rem"}}
                        >
                            Rename
                        </Button>
                        <Button
                            isBasic
                            isDanger
                            disabled={!canDeleteSelected}
                            onClick={openDeleteSelectedUnitTypes}
                            style={{marginLeft: "0.5rem"}}
                        >
                            Delete
                        </Button>
                        <Button
                            onClick={() => {
                                const hasPmsUnitTypes = (parsedData?.pmsData?.pmsUnitTypes.length ?? 0) > 0;
                                setUnitTypeModal({
                                    title: hasPmsUnitTypes ? "Add Unit Type(s)" : "Add Unit Type"
                                });
                            }}
                            style={{marginLeft: "0.5rem"}}
                            disabled={parsedData == undefined}
                        >
                            Add
                        </Button>
                        {/* <Button
                            isBasic
                            onClick={() => setUnitTypeModal({title: "Add Custom Unit Type"})}
                            style={{marginLeft: "0.25rem"}}
                        >
                            <PlusIcon />
                            <span style={{marginLeft: "0.25rem"}}>Custom</span>
                        </Button> */}
                    </>
                }
            >
                <div className={css.unitTypesTableWrapper}>
                    <SimpleHoTWrapper
                        withControls={true}
                        rows={tableRowsDisplay}
                        columns={tableColumns}
                        dataLoading={unitTypesDataLoading}
                        hotProps={{
                            renderAllRows: true,
                            colHeaders: ["", "Name", "Unit Count", "Occupancy %", "In Place Rent"],
                            colWidths: [20, 280, 120, 120, 120],
                            width: "100%",
                            height: "60vh",
                            stretchH: "all",
                            fixedColumnsStart: 2,
                            autoRowSize: false,
                            autoColumnSize: false,
                            rowHeights: 40,
                            afterInit: function(this: Handsontable) {
                                hotRef.current = this;
                            }
                        }}
                        beforeChange={handleBeforeChange}
                        onChange={handleChange}
                        contextMenu={contextMenu}
                        cellStyler={(_row, col, rowData) => {
                            const typed = rowData as UnitTypeTableRow;
                            const classes: string[] = [];
                            if(col === COL_SELECT) {
                                classes.push(css.selectCell);
                            }
                            if(col === COL_NAME) {
                                classes.push(css.nameCellAlign);
                            }
                            if(metricHasOverride(typed, col)) {
                                classes.push(css.isOverride);
                            }
                            return {
                                isReadOnly: col === COL_SELECT || col === COL_NAME,
                                cssClasses: classes.length > 0 ? classes : undefined
                            };
                        }}
                        customRenderer={(rowData, col) => {
                            const typed = rowData as UnitTypeTableRow;
                            const colIdx = columnIndex(col);

                            if(colIdx === COL_SELECT) {
                                return function(_hot, td): void {
                                    td.innerHTML = "";
                                    td.classList.add(css.selectCell);
                                    const input = document.createElement("input");
                                    input.type = "checkbox";
                                    input.checked = selectedUnitTypeIds.has(typed.extraData.id);
                                    input.title = "Select unit type";
                                    input.addEventListener("mousedown", e => {
                                        e.stopPropagation();
                                    });
                                    input.addEventListener("mouseup", e => {
                                        e.stopPropagation();
                                    });
                                    input.addEventListener("click", e => {
                                        e.stopPropagation();
                                        toggleUnitTypeSelected(typed.extraData.id);
                                    });
                                    td.appendChild(input);
                                } as BaseRenderer;
                            }

                            if(colIdx !== COL_NAME || typed.extraData.inAlert !== 1) {
                                return undefined;
                            }
                            return function(_hot, td): void {
                                if(td.querySelector(`.${css.nameAlertIcon}`)) {
                                    return;
                                }
                                const templateSvg = pmsAlertIconTemplateRef.current?.querySelector("svg");
                                if(!templateSvg) {
                                    return;
                                }
                                const icon = document.createElement("span");
                                icon.className = css.nameAlertIcon;
                                icon.title = "Unit type name not found in PMS";
                                icon.appendChild(templateSvg.cloneNode(true));
                                td.prepend(icon);
                            } as BaseRenderer;
                        }}
                    />
                </div>
            </Card>
            {
                unitTypeModal && property &&
                <EditUnitType
                    existingUnitTypes={unitTypesData?.listUnitTypes ?? []}
                    pmsUnitTypes={parsedData?.pmsData?.pmsUnitTypes}
                    pmsUnitTypesAsOfDate={parsedData?.pmsData?.pmsUnitTypesAsOfDate}
                    initialName={unitTypeModal.initialName}
                    unitTypeId={unitTypeModal.unitTypeId}
                    title={unitTypeModal.title}
                    onCancel={() => setUnitTypeModal(undefined)}
                    onSubmit={handleUnitTypeModalSubmit}
                />
            }
            {deleteUnitTypeData.length > 0 && property && (
                <ConfirmationModal
                    onAccept={() => handleDeleteUnitTypes(deleteUnitTypeData)}
                    onCancel={() => setDeleteUnitTypeData([])}
                    header="Are you sure you want to delete the selected unit types?"
                    message={`This will delete ${deleteUnitTypeData.length} unit types. This action cannot be undone.`}
                    confirmationText="DELETE"
                />
            )}
        </>
    );
}
